import { useState, useEffect, useCallback } from 'react';
import { fetchFlujoCaja, updateFlujoConfig, fetchMonthDetail, fetchMonthStatuses, fetchCashRadar } from '@/lib/api';
import { formatMXN, formatMXNCompact, formatDate } from '@/lib/formatters';
import { PageHeader } from '@/components/common/PageHeader';
import { KPICard } from '@/components/common/KPICard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, Wallet, AlertTriangle, RefreshCw,
  CreditCard, Pencil, Check, X as XIcon, Calendar, Settings2, Lock, CheckCircle2,
  Clock, DollarSign, Zap,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-4 py-3 text-sm">
      <p className="font-semibold text-slate-900 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="tabular-nums" style={{ color: p.color }}>{p.name}: {formatMXN(p.value)}</p>
      ))}
    </div>
  );
};

/* Row definitions for the transposed table */
const FLOW_ROWS = [
  { key: 'section_proy', label: 'FLUJO PROYECTADO', section: true, color: 'text-slate-600' },
  { key: 'proy_ingresos', label: 'Ingresos Proyectados', indent: true, color: 'text-emerald-700' },
  { key: 'proy_egresos', label: 'Egresos Proyectados', indent: true },
  { key: 'proy_flujo', label: 'Flujo Proyectado', bold: true, signed: true },
  { key: 'proy_caja', label: 'Caja Proyectada', bold: true, bgClass: 'bg-slate-50/60', signed: true },
  { key: 'divider1', label: '', divider: true },
  { key: 'section_real', label: 'FLUJO REAL', section: true, color: 'text-[#F05A28]' },
  { key: 'real_ingresos', label: 'Ingresos Reales', indent: true, color: 'text-emerald-600' },
  { key: 'real_egresos', label: 'Egresos Reales', indent: true },
  { key: 'real_flujo', label: 'Flujo Real', bold: true, signed: true, color: 'text-[#F05A28]' },
  { key: 'real_caja', label: 'Caja Real', bold: true, bgClass: 'bg-[#F05A28]/5', color: 'text-[#F05A28]', signed: true },
  { key: 'divider2', label: '', divider: true },
  { key: 'section_comp', label: 'VARIACIONES', section: true, color: 'text-slate-500' },
  { key: 'dif_ingresos', label: 'Dif. Ingresos (Real - Proy)', indent: true, signed: true },
  { key: 'dif_egresos', label: 'Dif. Egresos (Real - Proy)', indent: true, signed: true, invertColor: true },
  { key: 'dif_caja', label: 'Dif. Caja', indent: true, signed: true, bold: true },
  { key: 'cumplimiento', label: 'Cumplimiento Ingresos %', indent: true, suffix: '%' },
  { key: 'divider3', label: '', divider: true },
  { key: 'section_credito', label: 'CRÉDITO', section: true, color: 'text-[#162A45]' },
  { key: 'credito_final', label: 'Saldo Crédito', bold: true, color: 'text-[#162A45]' },
];

export default function FlujoCaja() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthStatuses, setMonthStatuses] = useState({});

  // Config editing
  const [editingConfig, setEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({ saldo_inicial: '', periodo_inicio: '', periodo_fin: '' });

  // Detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMeta, setDetailMeta] = useState({ mes: '', tipo: '', label: '' });

  const TIPO_MAP = {
    proy_ingresos: 'ingresos_proy',
    real_ingresos: 'ingresos_real',
    proy_egresos: 'egresos_proy',
    real_egresos: 'egresos_real',
  };
  const TIPO_LABELS = {
    ingresos_proy: 'Ingresos Proyectados',
    ingresos_real: 'Ingresos Reales',
    egresos_proy: 'Egresos Proyectados',
    egresos_real: 'Egresos Reales',
  };

  const openDetail = async (mes, mesLabel, rowKey, monthData) => {
    const tipo = TIPO_MAP[rowKey];
    if (!tipo) return;
    setDetailMeta({ mes, tipo, label: `${TIPO_LABELS[tipo]} — ${mesLabel}` });
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      if (tipo.startsWith('egresos') && monthData) {
        // Build breakdown from flujo data (single source of truth)
        const isReal = tipo === 'egresos_real';
        const prefix = isReal ? 'real' : 'proy';
        const items = [];
        const gastos = monthData[`${prefix}_gastos_op`] || 0;
        if (gastos > 0) items.push({ categoria: 'Gastos operativos', monto: gastos });
        const intereses = monthData[`${prefix}_intereses`] || 0;
        if (intereses > 0) items.push({ categoria: 'Intereses de crédito', monto: intereses });
        const liber = monthData[`${prefix}_liberaciones`] || 0;
        if (liber > 0) items.push({ categoria: 'Liberaciones de crédito', monto: liber });
        const prest = monthData[`${prefix}_prestamos`] || 0;
        if (prest > 0) items.push({ categoria: 'Préstamos', monto: prest });
        const total = items.reduce((s, i) => s + i.monto, 0);
        setDetailData({ items, total: Math.round(total * 100) / 100, count: items.length });
        // Also fetch the gastos breakdown from the endpoint for detail
        try {
          const detail = await fetchMonthDetail(mes, tipo);
          // Replace "Gastos operativos" with the individual categories
          const gastoItems = detail.items.filter(i => !['Intereses de crédito', 'Liberaciones de crédito', 'Préstamos'].includes(i.categoria));
          const nonGasto = items.filter(i => i.categoria !== 'Gastos operativos');
          setDetailData({ items: [...gastoItems, ...nonGasto], total: Math.round(total * 100) / 100, count: gastoItems.length + nonGasto.length });
        } catch {}
      } else {
        setDetailData(await fetchMonthDetail(mes, tipo));
      }
    } catch { setDetailData(null); }
    finally { setDetailLoading(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([fetchFlujoCaja(), fetchMonthStatuses().catch(() => ({}))])
      .then(([d, ms]) => {
        setData(d);
        setMonthStatuses(ms);
        setConfigForm({
          saldo_inicial: d.saldo_inicial_caja,
          periodo_inicio: d.periodo_inicio,
          periodo_fin: d.periodo_fin || '',
        });
      })
      .catch(() => toast.error('Error al calcular flujo'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    try {
      await updateFlujoConfig({
        saldo_inicial: parseFloat(configForm.saldo_inicial) || 0,
        periodo_inicio: configForm.periodo_inicio,
        periodo_fin: configForm.periodo_fin,
      });
      setEditingConfig(false);
      toast.success('Configuración actualizada');
      load();
    } catch { toast.error('Error al actualizar'); }
  };

  if (loading) return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-48" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      <Skeleton className="h-24 rounded-xl" />
      <Skeleton className="h-64 rounded-xl" />
      <Skeleton className="h-[400px] rounded-xl" />
    </div>
  );

  const r = data?.resumen || {};
  const meses = data?.meses || [];
  const chartData = meses.map(m => ({ mes: m.mes_label, proy_caja: m.proy_caja, real_caja: m.real_caja, credito: m.credito_final }));
  const cumplimiento = r.cumplimiento_global || 0;

  return (
    <div className="space-y-6" data-testid="flujo-caja-page">
      <PageHeader title="Flujo de Caja" subtitle="Herramienta ejecutiva de proyección mensual">
        <Button variant="outline" size="sm" onClick={() => load()} data-testid="flujo-caja-recalcular-button">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Recalcular
        </Button>
      </PageHeader>

      <Tabs defaultValue="flujo">
        <TabsList>
          <TabsTrigger value="flujo"><Wallet className="h-3.5 w-3.5 mr-1.5" />Flujo Mensual</TabsTrigger>
          <TabsTrigger value="radar"><Zap className="h-3.5 w-3.5 mr-1.5" />Radar de Caja</TabsTrigger>
        </TabsList>
        <TabsContent value="flujo" className="space-y-6 mt-4">

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPICard label="Caja Proyectada" value={formatMXNCompact(r.proy_caja_final)} icon={Wallet} className={r.proy_caja_final < 0 ? 'border-red-200' : ''} compact testId="flujo-kpi-caja-proy" />
        <KPICard label="Caja Real" value={formatMXNCompact(r.real_caja_final)} icon={Wallet} className={r.real_caja_final < 0 ? 'border-red-200' : 'border-emerald-200'} compact testId="flujo-kpi-caja-real" />
        <KPICard label="Ing. Proyectados" value={formatMXNCompact(r.proy_ingresos)} icon={TrendingUp} compact testId="flujo-kpi-ing-proy" />
        <KPICard label="Ing. Reales" value={formatMXNCompact(r.real_ingresos)} icon={TrendingUp} compact testId="flujo-kpi-ing-real" />
        <KPICard label="Cumplimiento" value={`${cumplimiento}%`} icon={cumplimiento >= 80 ? TrendingUp : AlertTriangle} className={cumplimiento < 50 ? 'border-red-200' : cumplimiento < 80 ? 'border-amber-200' : 'border-emerald-200'} compact testId="flujo-kpi-cumplimiento" />
        <KPICard label="Crédito Final" value={formatMXNCompact(r.credito_final)} icon={CreditCard} compact testId="flujo-kpi-credito" />
      </div>

      {/* ── Config Card: Fecha Inicio + Saldo Inicial ── */}
      <Card className="rounded-xl border bg-white">
        <CardContent className="p-4">
          {editingConfig ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-[#F05A28]" />
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Configuración del Periodo</p>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[120px]">
                  <label className="text-[10px] font-medium text-slate-500 mb-1 block">Inicio</label>
                  <Input type="month" value={configForm.periodo_inicio} onChange={e => setConfigForm(f => ({ ...f, periodo_inicio: e.target.value }))} className="h-9 text-xs w-full" data-testid="config-periodo-inicio" />
                </div>
                <div className="min-w-[120px]">
                  <label className="text-[10px] font-medium text-slate-500 mb-1 block">Fin</label>
                  <Input type="month" value={configForm.periodo_fin} onChange={e => setConfigForm(f => ({ ...f, periodo_fin: e.target.value }))} className="h-9 text-xs w-full" data-testid="config-periodo-fin" />
                </div>
                <div className="min-w-[160px] flex-1 max-w-[220px]">
                  <label className="text-[10px] font-medium text-slate-500 mb-1 block">Saldo Inicial (MXN)</label>
                  <Input type="number" value={configForm.saldo_inicial} onChange={e => setConfigForm(f => ({ ...f, saldo_inicial: e.target.value }))} className="h-9 text-xs w-full" data-testid="config-saldo-inicial" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="h-9" onClick={() => setEditingConfig(false)}>
                    <XIcon className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" className="h-9" onClick={saveConfig} data-testid="config-save">
                    <Check className="h-3.5 w-3.5 mr-1" /> Guardar
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Periodo</p>
                  <p className="text-lg font-bold text-slate-900 mt-0.5 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    <Calendar className="h-4 w-4 text-[#F05A28]" />
                    {formatDate(data?.periodo_inicio) || '—'} <span className="text-slate-300 text-sm font-normal">a</span> {formatDate(data?.periodo_fin) || '—'}
                  </p>
                </div>
                <div className="h-10 w-px bg-slate-200 hidden sm:block" />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Saldo Inicial de Caja</p>
                  <p className="text-lg font-bold tabular-nums text-slate-900 mt-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {formatMXN(data?.saldo_inicial_caja)}
                  </p>
                </div>
                <div className="h-10 w-px bg-slate-200 hidden sm:block" />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Meses</p>
                  <p className="text-lg font-bold text-slate-900 mt-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {data?.horizonte || 0}
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setEditingConfig(true)} data-testid="config-edit">
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chart: Projected vs Real */}
      <Card className="rounded-xl border bg-white" data-testid="flujo-chart">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-700">Caja Proyectada vs Real</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={0.6} />
              <XAxis dataKey="mes" tick={{ fontSize: 10, fill: '#64748B' }} interval="preserveStartEnd" />
              <YAxis tickFormatter={v => `$${(v / 1e6).toFixed(1)}M`} tick={{ fontSize: 10, fill: '#64748B' }} />
              <RechartsTooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="4 4" strokeOpacity={0.4} />
              <Line type="monotone" dataKey="proy_caja" name="Caja Proyectada" stroke="#94A3B8" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 2, fill: '#94A3B8' }} />
              <Line type="monotone" dataKey="real_caja" name="Caja Real" stroke="#F05A28" strokeWidth={2.5} dot={{ r: 2.5, fill: '#F05A28' }} />
              <Line type="monotone" dataKey="credito" name="Crédito" stroke="#162A45" strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 1.5, fill: '#162A45' }} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Calendar Table (months as columns) */}
      <Card className="rounded-xl border bg-white overflow-hidden">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-slate-700">Proyección Mensual</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto">
            <table className="w-full text-[12px]" data-testid="flujo-caja-table">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-4 text-left sticky-col-header min-w-[200px]">Concepto</th>
                  {meses.map(m => {
                    const neg = m.caja_final < 0;
                    const st = monthStatuses[m.mes] || 'abierto';
                    const isCurrentMonth = m.mes === new Date().toISOString().substring(0, 7);
                    return (
                      <th key={m.mes} className={`text-[10px] uppercase tracking-wide font-semibold py-2.5 px-2 text-right whitespace-nowrap min-w-[110px] ${neg ? 'text-red-500' : 'text-slate-500'} ${isCurrentMonth ? 'bg-[#F05A28]/5 border-b-2 border-[#F05A28]' : st === 'cerrado' ? 'bg-slate-100' : st === 'validado' ? 'bg-emerald-50/50' : ''}`}>
                        <div className={isCurrentMonth ? 'text-[#F05A28] font-bold' : ''}>{m.mes_label.split(' ')[0]}</div>
                        <div className={`text-[9px] font-normal ${isCurrentMonth ? 'text-[#F05A28]/60' : 'text-slate-400'}`}>{m.mes_label.split(' ')[1]}</div>
                      </th>
                    );
                  })}
                  <th className="text-[10px] uppercase tracking-widest text-slate-600 font-bold py-3 px-4 text-right bg-slate-100/60 min-w-[120px]">Total</th>
                </tr>
              </thead>
              <tbody>
                {FLOW_ROWS.map(row => {
                  if (row.divider) return <tr key={row.key}><td colSpan={meses.length + 2} className="h-px bg-slate-200" /></tr>;
                  if (row.section) return (
                    <tr key={row.key} className="bg-slate-50/40">
                      <td className={`py-2.5 px-4 text-[10px] font-bold uppercase tracking-widest sticky-col-accent ${row.color}`} style={{ backgroundColor: 'hsl(210, 40%, 97.5%)' }}>{row.label}</td>
                      {meses.map(m => <td key={m.mes} className="py-2.5" style={{ backgroundColor: 'hsl(210, 40%, 97.5%)' }} />)}
                      <td className="py-2.5" style={{ backgroundColor: 'hsl(210, 40%, 97.5%)' }} />
                    </tr>
                  );

                  const rowTotal = (() => {
                    switch(row.key) {
                      case 'proy_ingresos': return r.proy_ingresos;
                      case 'proy_egresos': return r.proy_egresos;
                      case 'proy_flujo': return r.proy_flujo_neto;
                      case 'proy_caja': return r.proy_caja_final;
                      case 'real_ingresos': return r.real_ingresos;
                      case 'real_egresos': return r.real_egresos;
                      case 'real_flujo': return r.real_flujo_neto;
                      case 'real_caja': return r.real_caja_final;
                      case 'dif_ingresos': return r.dif_ingresos;
                      case 'dif_egresos': return (r.real_egresos || 0) - (r.proy_egresos || 0);
                      case 'dif_caja': return (r.real_caja_final || 0) - (r.proy_caja_final || 0);
                      case 'cumplimiento': return r.cumplimiento_global;
                      case 'credito_final': return r.credito_final;
                      default: return 0;
                    }
                  })();

                  return (
                    <tr key={row.key} className={`border-b last:border-b-0 ${row.bgClass || ''}`}>
                      <td className={`py-2.5 px-4 text-[12px] sticky-col ${row.bold ? 'font-bold' : ''} ${row.indent ? 'pl-7' : ''} ${row.color || 'text-slate-700'}`}>
                        {row.label}
                      </td>
                      {meses.map(m => {
                        const val = m[row.key] || 0;
                        const isEmpty = val === 0 && row.positiveOnly;
                        const isNeg = row.invertColor ? (val > 0) : (val < 0);
                        const isPos = row.invertColor ? (val < 0) : (val > 0);
                        const showColor = row.signed && val !== 0;
                        const isClickable = TIPO_MAP[row.key] && val !== 0;
                        const isCurrentMonth = m.mes === new Date().toISOString().substring(0, 7);
                        const currentBg = isCurrentMonth ? 'bg-[#F05A28]/[0.03]' : '';

                        const cellContent = isEmpty ? (
                          <span className="text-slate-200">—</span>
                        ) : row.suffix ? (
                          <span className={`${row.color || ''}`}>{val}{row.suffix}</span>
                        ) : (
                          <span className={`${showColor ? (isNeg ? 'text-red-500' : isPos ? 'text-emerald-600' : '') : ''} ${(!showColor && row.color) ? row.color : ''}`}>
                            {formatMXN(val)}
                          </span>
                        );

                        if (isClickable) {
                          return (
                            <td key={m.mes} className={`py-2.5 px-2 text-right tabular-nums whitespace-nowrap text-[11px] cursor-pointer hover:bg-slate-100 transition-colors ${row.bold ? 'font-bold' : ''} ${row.bgClass || ''} ${currentBg}`}
                              onClick={() => openDetail(m.mes, m.mes_label, row.key, m)}
                              title="Click para ver desglose"
                            >
                              {cellContent}
                            </td>
                          );
                        }

                        return (
                          <td key={m.mes} className={`py-2.5 px-2 text-right tabular-nums whitespace-nowrap text-[11px] ${row.bold ? 'font-bold' : ''} ${row.bgClass || ''} ${currentBg}`}>
                            {cellContent}
                          </td>
                        );
                      })}
                      <td className={`py-2.5 px-3 text-right tabular-nums whitespace-nowrap text-[11px] font-bold bg-slate-100/60 ${
                        row.signed && rowTotal !== 0
                          ? (row.invertColor
                            ? (rowTotal > 0 ? 'text-red-500' : 'text-emerald-600')
                            : (rowTotal < 0 ? 'text-red-500' : 'text-emerald-600'))
                          : (row.color || 'text-slate-900')
                      }`}>
                        {row.suffix ? `${rowTotal}${row.suffix}` : formatMXN(rowTotal)}
                      </td>
                    </tr>
                  );
                })}
                {/* Month Status Row */}
                <tr className="border-t-2 border-slate-300">
                  <td className="py-3 px-4 text-[10px] font-bold uppercase tracking-widest text-slate-500 sticky-col" style={{ backgroundColor: 'white' }}>Estatus</td>
                  {meses.map(m => {
                    const st = monthStatuses[m.mes] || 'abierto';
                    return (
                      <td key={m.mes} className="py-2 px-2 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-semibold ${
                          st === 'cerrado' ? 'bg-slate-200 text-slate-600' :
                          st === 'validado' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          'text-slate-400'
                        }`}>
                          {st === 'cerrado' && <Lock className="h-3 w-3" />}
                          {st === 'validado' && <CheckCircle2 className="h-3 w-3" />}
                          {st === 'cerrado' ? 'Cerrado' : st === 'validado' ? 'Validado' : '—'}
                        </span>
                      </td>
                    );
                  })}
                  <td className="bg-slate-100/60" />
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards: Projected vs Real */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="rounded-xl border bg-white">
          <CardContent className="p-4">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-3">Proyectado (Total Periodo)</p>
            <div className="space-y-2">
              <SRow label="Ingresos proyectados" value={r.proy_ingresos} color="text-emerald-700" />
              <SRow label="Egresos proyectados" value={r.proy_egresos} />
              <Separator />
              <SRow label="Flujo neto proyectado" value={r.proy_flujo_neto} bold signed />
              <SRow label="Caja final proyectada" value={r.proy_caja_final} bold signed />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border bg-white">
          <CardContent className="p-4">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-[#F05A28] mb-3">Real (Total Periodo)</p>
            <div className="space-y-2">
              <SRow label="Ingresos reales" value={r.real_ingresos} color="text-emerald-600" />
              <SRow label="Egresos reales" value={r.real_egresos} />
              <Separator />
              <SRow label="Flujo neto real" value={r.real_flujo_neto} bold signed />
              <SRow label="Caja final real" value={r.real_caja_final} bold signed />
              <div className="pt-1">
                <SRow label="Cumplimiento ingresos" valueText={`${r.cumplimiento_global || 0}%`} />
                <SRow label="Desviación egresos" value={r.dif_egresos} signed />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={v => { if (!v) { setDetailOpen(false); setDetailData(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm">{detailMeta.label}</DialogTitle>
          </DialogHeader>
          {detailLoading ? <Skeleton className="h-40" /> : detailData ? (
            <div className="space-y-3 py-2">
              <div className="flex justify-between items-center bg-slate-50 rounded-lg p-3">
                <span className="text-xs text-slate-500">{detailData.count} {detailMeta.tipo?.startsWith('ingresos') ? 'pagos' : 'conceptos'}</span>
                <span className="text-[15px] font-bold tabular-nums text-[#F05A28]">{formatMXN(detailData.total)}</span>
              </div>
              {detailData.items.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      {detailMeta.tipo?.startsWith('ingresos') ? (
                        <>
                          <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-2">Vivienda</TableHead>
                          <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-2">Cliente</TableHead>
                          <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-2">Tipo</TableHead>
                          <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-2 text-right">Monto</TableHead>
                        </>
                      ) : (
                        <>
                          <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-2">Categoría</TableHead>
                          <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-2 text-right">Monto</TableHead>
                        </>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailData.items.map((item, i) => (
                      <TableRow key={i}>
                        {detailMeta.tipo?.startsWith('ingresos') ? (
                          <>
                            <TableCell className="py-1.5 px-2 text-[11px] font-medium">{item.vivienda}</TableCell>
                            <TableCell className="py-1.5 px-2 text-[11px] text-slate-500 truncate max-w-[120px]">{item.cliente}</TableCell>
                            <TableCell className="py-1.5 px-2"><Badge variant="outline" className="text-[8px]">{item.tipo_pago === 'anticipo' ? 'Anticipo' : item.tipo_pago === 'finiquito' ? 'Finiquito' : 'Intermedio'}</Badge></TableCell>
                            <TableCell className="py-1.5 px-2 text-right text-[11px] tabular-nums font-semibold">{formatMXN(item.monto)}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="py-1.5 px-2 text-[11px] font-medium">{item.categoria}</TableCell>
                            <TableCell className="py-1.5 px-2 text-right text-[11px] tabular-nums font-semibold">{formatMXN(item.monto)}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <p className="text-xs text-slate-400 text-center py-4">Sin registros para este periodo</p>}
            </div>
          ) : <p className="text-xs text-slate-400 text-center py-4">Error al cargar detalle</p>}
        </DialogContent>
      </Dialog>
        </TabsContent>
        <TabsContent value="radar" className="mt-4">
          <CashRadarView />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ══════════════════════════════════════════════
   CASH RADAR VIEW
   ══════════════════════════════════════════════ */
function CashRadarView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCashRadar().then(setData).catch(() => toast.error('Error')).finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton className="h-[400px] rounded-xl" />;
  if (!data) return null;

  const k = data.kpis;
  const pressureCfg = {
    estable: { label: 'Estable', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
    atencion: { label: 'Atención', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle },
    critico: { label: 'Crítico', cls: 'bg-red-50 text-red-600 border-red-200', icon: AlertTriangle },
  };
  const pc = pressureCfg[data.pressure] || pressureCfg.estable;

  return (
    <div className="space-y-6">
      {/* Pressure indicator */}
      <div className={`flex items-center gap-3 rounded-xl border p-4 ${pc.cls}`}>
        <pc.icon className="h-6 w-6" />
        <div>
          <p className="text-[14px] font-bold">{pc.label}</p>
          <p className="text-[11px] opacity-80">
            {data.pressure === 'critico' ? 'Cobros insuficientes para cubrir egresos comprometidos' :
             data.pressure === 'atencion' ? 'Pagos vencidos representan más del 30% de egresos' :
             'Cobros programados cubren egresos comprometidos'}
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
        <Card className="rounded-xl border bg-red-50/30 border-red-200"><CardContent className="p-3.5 text-center">
          <p className="text-[9px] uppercase tracking-widest text-red-500">Vencidos</p>
          <p className="text-[17px] font-bold tabular-nums text-red-600" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(k.overdue_total)}</p>
          <p className="text-[10px] text-red-400">{k.overdue_count} pagos</p>
        </CardContent></Card>
        <Card className="rounded-xl border"><CardContent className="p-3.5 text-center">
          <p className="text-[9px] uppercase tracking-widest text-slate-400">Próx. 7 días</p>
          <p className="text-[17px] font-bold tabular-nums text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(k.next_7_days)}</p>
        </CardContent></Card>
        <Card className="rounded-xl border"><CardContent className="p-3.5 text-center">
          <p className="text-[9px] uppercase tracking-widest text-slate-400">Próx. 15 días</p>
          <p className="text-[17px] font-bold tabular-nums text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(k.next_15_days)}</p>
        </CardContent></Card>
        <Card className="rounded-xl border"><CardContent className="p-3.5 text-center">
          <p className="text-[9px] uppercase tracking-widest text-[#F05A28]">Próx. 30 días</p>
          <p className="text-[17px] font-bold tabular-nums text-[#F05A28]" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(k.next_30_days)}</p>
          <p className="text-[10px] text-slate-400">{k.upcoming_count} cobros</p>
        </CardContent></Card>
        <Card className="rounded-xl border"><CardContent className="p-3.5 text-center">
          <p className="text-[9px] uppercase tracking-widest text-slate-400">Egresos Comprometidos</p>
          <p className="text-[17px] font-bold tabular-nums text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(k.committed_expenses)}</p>
          <p className="text-[10px] text-slate-400">mes actual</p>
        </CardContent></Card>
      </div>

      {/* Overdue */}
      {data.overdue.length > 0 && (
        <Card className="rounded-xl border border-red-200 bg-white overflow-clip">
          <CardHeader className="py-3 px-4 bg-red-50/50 border-b">
            <CardTitle className="text-sm text-red-600 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Pagos Vencidos ({data.overdue.length})</CardTitle>
          </CardHeader>
          <Table>
            <TableHeader>
              <TableRow className="bg-red-50/30">
                <TableHead className="text-[9px] uppercase text-red-400 py-2 px-3">Vivienda</TableHead>
                <TableHead className="text-[9px] uppercase text-red-400 py-2 px-3">Cliente</TableHead>
                <TableHead className="text-[9px] uppercase text-red-400 py-2 px-3">Tipo</TableHead>
                <TableHead className="text-[9px] uppercase text-red-400 py-2 px-3 text-right">Pendiente</TableHead>
                <TableHead className="text-[9px] uppercase text-red-400 py-2 px-3 text-right">Días</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.overdue.map(e => (
                <TableRow key={e.payment_id}>
                  <TableCell className="py-1.5 px-3 text-[11px] font-medium">{e.vivienda}</TableCell>
                  <TableCell className="py-1.5 px-3 text-[11px] text-slate-500 truncate max-w-[140px]">{e.cliente}</TableCell>
                  <TableCell className="py-1.5 px-3"><Badge variant="outline" className="text-[8px]">{e.tipo_pago === 'anticipo' ? 'Anticipo' : e.tipo_pago === 'finiquito' ? 'Finiquito' : 'Intermedio'}</Badge></TableCell>
                  <TableCell className="py-1.5 px-3 text-right text-[11px] tabular-nums font-bold text-red-600">{formatMXN(e.pending_amount)}</TableCell>
                  <TableCell className="py-1.5 px-3 text-right text-[11px] tabular-nums text-red-500">{Math.abs(e.days_until || 0)}d</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Upcoming */}
      <Card className="rounded-xl border bg-white overflow-clip">
        <CardHeader className="py-3 px-4 bg-slate-50/50 border-b">
          <CardTitle className="text-sm text-slate-700 flex items-center gap-2"><Clock className="h-4 w-4 text-[#F05A28]" /> Próximos Cobros — 30 días ({data.upcoming.length})</CardTitle>
        </CardHeader>
        {data.upcoming.length === 0 ? (
          <CardContent className="py-8 text-center"><p className="text-sm text-slate-400">Sin cobros programados en los próximos 30 días</p></CardContent>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/30">
                <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-3">Fecha</TableHead>
                <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-3">Vivienda</TableHead>
                <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-3">Cliente</TableHead>
                <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-3">Tipo</TableHead>
                <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-3 text-right">Pendiente</TableHead>
                <TableHead className="text-[9px] uppercase text-slate-400 py-2 px-3 text-right">En</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.upcoming.map(e => (
                <TableRow key={e.payment_id} className={e.days_until <= 7 ? 'bg-emerald-50/30' : ''}>
                  <TableCell className="py-1.5 px-3 text-[11px] tabular-nums font-medium">{formatDate(e.scheduled_date)}</TableCell>
                  <TableCell className="py-1.5 px-3 text-[11px] font-medium">{e.vivienda}</TableCell>
                  <TableCell className="py-1.5 px-3 text-[11px] text-slate-500 truncate max-w-[140px]">{e.cliente}</TableCell>
                  <TableCell className="py-1.5 px-3"><Badge variant="outline" className="text-[8px]">{e.tipo_pago === 'anticipo' ? 'Anticipo' : e.tipo_pago === 'finiquito' ? 'Finiquito' : 'Intermedio'}</Badge></TableCell>
                  <TableCell className="py-1.5 px-3 text-right text-[12px] tabular-nums font-bold text-[#F05A28]">{formatMXN(e.pending_amount)}</TableCell>
                  <TableCell className="py-1.5 px-3 text-right text-[11px] tabular-nums">{e.days_until}d</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function SRow({ label, value, valueText, bold, signed, color }) {
  const neg = signed && value < 0;
  const pos = signed && value > 0;
  return (
    <div className={`flex justify-between text-sm ${bold ? 'font-bold' : ''}`}>
      <span className="text-slate-500">{label}</span>
      <span className={`tabular-nums ${bold ? 'text-slate-900' : 'font-medium'} ${neg ? 'value-negative' : ''} ${pos ? 'value-positive' : ''} ${color || ''}`}>
        {valueText || formatMXN(value)}
      </span>
    </div>
  );
}
